/**
 * HarvestPipelineStatus — visual pipeline progress panel for the harvest workflow.
 * Shows a horizontal step indicator, specialty-level checklist, live elapsed timer,
 * and enriched sub-status messages during each pipeline phase.
 *
 * All data comes from the HarvestStatusDocument returned by the backend.
 * This component is purely presentational — no API calls.
 */

import { useState, useEffect, Fragment } from 'react';
import { Check, X, Pause, Clock, AlertTriangle } from 'lucide-react';

/** Pipeline step definitions (left to right). */
const STEPS = [
    { key: 'step1', label: 'Step 1', sublabel: 'SOURCE LOOKUPS' },
    { key: 'review', label: 'Review', sublabel: 'GAP ANALYSIS' },
    { key: 'step2', label: 'Step 2', sublabel: 'LLM ENRICHMENT' },
    { key: 'done', label: 'Complete', sublabel: '' },
];

/** Statuses where the pipeline is actively running. */
const ACTIVE_STATUSES = [
    'running', 'paused', 'step1_complete', 'batch_submitted',
    'step2_batch_submitted', 'step2_batch_submitting', 'step2_preparing',
    'batch_polling', 'step2_distributing',
];

/**
 * Derives per-step visual state from the harvest status document.
 * @param {object} status - HarvestStatusDocument from the API
 * @returns {string[]} Array of 4 step states: 'completed' | 'active' | 'pending' | 'failed' | 'paused' | 'stopped' | 'skipped'
 */
const getStepStates = (status) => {
    if (!status) return ['pending', 'pending', 'pending', 'pending'];
    const s = status.status;
    const step = status.currentStep;

    switch (s) {
        case 'running':
            if (step === 'step2') return ['completed', 'completed', 'active', 'pending'];
            return ['active', 'pending', 'pending', 'pending'];
        case 'paused':
            if (step === 'step2') return ['completed', 'completed', 'paused', 'pending'];
            return ['paused', 'pending', 'pending', 'pending'];
        case 'step1_complete':
            return ['completed', 'active', 'pending', 'pending'];
        case 'step2_preparing':
        case 'batch_submitted':
        case 'step2_batch_submitted':
        case 'step2_batch_submitting':
        case 'batch_polling':
        case 'step2_distributing':
            return ['completed', 'completed', 'active', 'pending'];
        case 'completed':
            return ['completed', 'completed', 'completed', 'completed'];
        case 'completed_step1_only':
            return ['completed', 'skipped', 'skipped', 'completed'];
        case 'failed':
            if (step === 'step2') return ['completed', 'completed', 'failed', 'pending'];
            return ['failed', 'pending', 'pending', 'pending'];
        case 'stopped':
            if (step === 'step2') return ['completed', 'completed', 'stopped', 'pending'];
            return ['stopped', 'pending', 'pending', 'pending'];
        default:
            return ['pending', 'pending', 'pending', 'pending'];
    }
};

/**
 * Returns a human-readable sub-status description for Step 2 batch states.
 * @param {object} status - HarvestStatusDocument
 * @returns {string}
 */
const getStep2SubStatus = (status) => {
    switch (status.status) {
        case 'batch_submitted':
        case 'step2_batch_submitted':
            return 'Batch pages submitted to Gemini API. Waiting for processing to begin\u2026';
        case 'batch_polling': {
            const parts = [];
            if (status.batchPagesTotal > 0) {
                parts.push(`${status.batchPagesCompleted}/${status.batchPagesTotal} pages complete`);
            }
            if (status.batchPollCount > 0) {
                parts.push(`poll cycle ${status.batchPollCount}`);
            }
            const detail = parts.length > 0 ? ` \u2014 ${parts.join(', ')}` : '\u2026';
            return `Gemini is processing translations${detail}`;
        }
        case 'step2_distributing':
            return 'Merging LLM translations into specialty glossaries\u2026';
        default:
            return '';
    }
};

/**
 * Renders the node content (icon or number) based on the step state.
 * @param {string} state - step state
 * @param {number} index - step index (0-based)
 * @returns {JSX.Element}
 */
const nodeContent = (state, index) => {
    if (state === 'completed') return <Check size={14} strokeWidth={3} />;
    if (state === 'failed') return <X size={14} strokeWidth={3} />;
    if (state === 'paused') return <Pause size={12} />;
    if (state === 'skipped') return <span className="dl-node-dash">&ndash;</span>;
    return <span className="dl-node-number">{index + 1}</span>;
};

/**
 * PipelineStepper — horizontal step indicator with connecting lines.
 * @param {{ states: string[] }} props
 */
const PipelineStepper = ({ states }) => (
    <div className="dl-pipeline-stepper" data-testid="harvest-pipeline-stepper">
        {STEPS.map((step, i) => (
            <Fragment key={step.key}>
                {i > 0 && (
                    <div
                        className={`dl-pipeline-connector ${
                            states[i - 1] === 'completed' ? 'dl-connector-done' : 'dl-connector-pending'
                        }`}
                        data-testid={`pipeline-connector-${i}`}
                    />
                )}
                <div className={`dl-pipeline-step dl-pstep-${states[i]}`}>
                    <div
                        className={`dl-pipeline-node dl-node-${states[i]}`}
                        data-testid={`pipeline-node-${step.key}`}
                        role="img"
                        aria-label={`${step.label}: ${states[i]}`}
                    >
                        {nodeContent(states[i], i)}
                    </div>
                    <span className="dl-pipeline-label">{step.label}</span>
                    {step.sublabel && <span className="dl-pipeline-sublabel">{step.sublabel}</span>}
                </div>
            </Fragment>
        ))}
    </div>
);

/**
 * SpecialtyProgress — compact horizontal checklist of specialty processing status during Step 1.
 * Shows completed/failed/active specialties by name, plus remaining count.
 * @param {{ status: object }} props
 */
const SpecialtyProgress = ({ status }) => {
    const completed = status.completedSpecialtyKeys || [];
    const failed = status.failedSpecialtyKeys || [];
    const current = status.currentSpecialty || '';
    const total = status.specialtiesTotal ?? 8;

    const items = [];
    completed.forEach(k => items.push({ key: k, state: 'completed' }));
    failed.forEach(k => items.push({ key: k, state: 'failed' }));
    if (current && !completed.includes(current) && !failed.includes(current)) {
        items.push({ key: current, state: 'active' });
    }

    const remaining = Math.max(0, total - items.length);

    if (items.length === 0 && remaining === 0) return null;

    return (
        <div className="dl-specialty-progress" data-testid="harvest-specialty-progress">
            <div className="dl-sp-items">
                {items.map(item => (
                    <span
                        key={item.key}
                        className={`dl-sp-item dl-sp-${item.state}`}
                        data-testid={`specialty-status-${item.key}`}
                    >
                        {item.state === 'completed' && <Check size={12} strokeWidth={3} />}
                        {item.state === 'failed' && <X size={12} strokeWidth={3} />}
                        {item.state === 'active' && <span className="dl-sp-dot" />}
                        <span className="dl-sp-name">{item.key}</span>
                    </span>
                ))}
                {remaining > 0 && (
                    <span className="dl-sp-remaining" data-testid="specialty-remaining">
                        {remaining} pending
                    </span>
                )}
            </div>
        </div>
    );
};

/**
 * ElapsedTimer — live ticking elapsed time since a given ISO timestamp.
 * Updates every second. Uses monospace font for stable digit width.
 * @param {{ startedAt: string }} props
 */
const ElapsedTimer = ({ startedAt }) => {
    const [elapsed, setElapsed] = useState('');

    useEffect(() => {
        if (!startedAt) return;
        const update = () => {
            const diff = Math.max(0, Date.now() - new Date(startedAt).getTime());
            const totalSec = Math.floor(diff / 1000);
            const h = Math.floor(totalSec / 3600);
            const m = Math.floor((totalSec % 3600) / 60);
            const s = totalSec % 60;
            setElapsed(
                h > 0
                    ? `${h}h ${m}m ${String(s).padStart(2, '0')}s`
                    : `${m}m ${String(s).padStart(2, '0')}s`
            );
        };
        update();
        const id = setInterval(update, 1000);
        return () => clearInterval(id);
    }, [startedAt]);

    if (!startedAt || !elapsed) return null;

    return (
        <span className="dl-elapsed-timer" data-testid="harvest-elapsed-timer">
            <Clock size={12} />
            <span>{elapsed}</span>
        </span>
    );
};

/**
 * HarvestPipelineStatus — unified status panel for the harvest pipeline.
 * Renders the pipeline stepper, specialty checklist (Step 1), sub-status messages
 * (Step 2), failed specialty callout, and live elapsed timer.
 *
 * Shown for all non-idle harvest states. Integrates above the existing banners
 * in DomainLibrary without replacing them.
 *
 * @param {{ status: object }} props - HarvestStatusDocument from the API
 */
const HarvestPipelineStatus = ({ status }) => {
    if (!status || status.status === 'idle') return null;

    const stepStates = getStepStates(status);
    const isActive = ACTIVE_STATUSES.includes(status.status);
    const isStep1Running = status.status === 'running' && status.currentStep !== 'step2';
    const isStep2Active = ['step2_preparing', 'batch_submitted', 'step2_batch_submitted', 'step2_batch_submitting', 'batch_polling', 'step2_distributing'].includes(status.status)
        || (status.status === 'running' && status.currentStep === 'step2');
    const isTerminalWithStep1Failures = (status.status === 'failed' || status.status === 'stopped')
        && status.currentStep !== 'step2';
    const hasFailed = (isStep1Running || isTerminalWithStep1Failures)
        && status.failedSpecialtyKeys && status.failedSpecialtyKeys.length > 0;
    const step2Sub = getStep2SubStatus(status);

    return (
        <div
            className="dl-pipeline-panel"
            data-testid="harvest-pipeline-panel"
            aria-live="polite"
            aria-atomic="false"
            aria-label="Harvest pipeline status"
        >
            <div className="dl-pipeline-top-row">
                <span className="dl-pipeline-title">Pipeline</span>
                {isActive && status.startedAt && (
                    <ElapsedTimer startedAt={status.startedAt} />
                )}
            </div>

            <PipelineStepper states={stepStates} />

            {isStep1Running && <SpecialtyProgress status={status} />}

            {hasFailed && (
                <div className="dl-pipeline-error-callout" data-testid="harvest-failed-specialties-callout">
                    <AlertTriangle size={14} />
                    <span>
                        {status.failedSpecialtyKeys.length} {status.failedSpecialtyKeys.length === 1 ? 'specialty' : 'specialties'} failed:{' '}
                        {status.failedSpecialtyKeys.join(', ')}
                    </span>
                </div>
            )}

            {isStep2Active && step2Sub && (
                <p className="dl-pipeline-substatus" data-testid="harvest-step2-substatus">
                    {step2Sub}
                </p>
            )}
        </div>
    );
};

export default HarvestPipelineStatus;
